import { track, trackRoute } from "./umami";
import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  Archive,
  ArrowLeftRight,
  ArrowRight,
  BadgeCheck,
  Bell,
  Building2,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Cloud,
  Copy,
  Database,
  Download,
  FileText,
  Folder,
  FolderSync,
  Gauge,
  Globe,
  History,
  KeyRound,
  LayoutDashboard,
  ListChecks,
  Lock,
  Mail,
  Menu,
  MessageSquareText,
  Network,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Server,
  ServerCog,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Terminal,
  TimerReset,
  Trash2,
  Users,
  Workflow,
  X,
} from "lucide-react";
import {
  checkoutUrl,
  configuredDownloadUrl,
  fetchLicenseForSession,
  fetchPublicConfig,
  resendLicense,
  type LicenseLookupResult,
  type PublicConfig,
} from "./bridge";
import { siteConfig } from "./config";
import { ContactSalesPage } from "./ContactSales";
import { DownloadOptions } from "./DownloadGate";
import { fetchLatestRelease, type ReleaseManifest } from "./releaseManifest";
import { PrivacyPage, TermsPage } from "./LegalPages";
import { applySeo, appSchema, faqSchema, type FaqItem, type SeoMeta } from "./seo";
import { landingBySlug } from "./marketing/landingData";
import { comparisonBySlug } from "./marketing/comparisonData";
import { ComparisonPageView, LandingPageView } from "./marketing/MarketingPages";
import {
  annualSavingsNote,
  annualToggleNote,
  availabilityFrom,
  availabilityText,
  billingNote,
  compactPrice,
  comparisonGroups,
  contactSalesPlan,
  countLimitValues,
  definition,
  featureLabel,
  freePlan,
  limitPhrase,
  midSentence,
  minimumAppVersion,
  minimumPlanFor,
  minimumPlanTag,
  planAnchor,
  planById,
  planDefinitions,
  planNameList,
  plans,
  plansWithFeature,
  priceAmount,
  priceUnit,
  selfServePlans,
  type BillingInterval,
  type CatalogCell,
  type CatalogRow,
  type Plan,
} from "./plans";
import "./styles.css";

type Route =
  | "home"
  | "pricing"
  | "download"
  | "activation"
  | "support"
  | "privacy"
  | "terms"
  | "contact"
  | "landing"
  | "comparison";

const homeFaq: FaqItem[] = [
  {
    q: "What is FileAgent?",
    a: "FileAgent is a self-hosted file transfer automation platform. It schedules, monitors, and audits the movement of files between local storage, SMB shares, SFTP, FTP, AWS S3, and Azure Blob — from a clean web UI you run on your own server.",
  },
  {
    q: "Who is FileAgent for?",
    a: "IT teams, system administrators, and operations, finance, and healthcare IT teams at SMBs and enterprises who need reliable, recurring file transfers without maintaining fragile scripts or buying a heavy enterprise MFT platform.",
  },
  {
    q: "How is FileAgent different from scripts and Scheduled Tasks?",
    a: "Scripts and Scheduled Tasks give you no shared visibility, no central credentials, and no audit trail. FileAgent replaces them with managed jobs in one console — scheduling, retries, alerts, run history, and roles included — so transfers are operable by your whole team.",
  },
  {
    q: "How is it different from enterprise MFT platforms?",
    a: "FileAgent delivers the controls most teams actually need — scheduling, credentials, logging, alerts, roles, and retention — without the licensing cost, deployment project, and operational complexity of a full enterprise MFT suite. It's the practical middle ground.",
  },
  {
    q: "Is FileAgent self-hosted?",
    a: "Yes. FileAgent installs on a Windows or Linux host you control, so your files move directly between your systems and stay inside your network. Only license validation and update checks use the internet.",
  },
  {
    q: "Which systems can FileAgent move files between?",
    a: `Local folders, SMB/network shares, FTP, SFTP, AWS S3, and Azure Blob, as sources or destinations, plus File to Email delivery. Copy and Move work on ${availabilityText("jobs.copyMove")} and Sync/Mirror on ${availabilityFrom("jobs.syncMirror")}. Available connectors depend on your plan: FTP and SFTP start on ${minimumPlanFor("connector.sftp").name}, and AWS S3 and Azure Blob on ${minimumPlanFor("connector.s3").name}.`,
  },
  {
    q: "Does FileAgent provide an audit trail?",
    a: "Every run is recorded with status, timing, file counts, sizes, and per-file detail, retained according to your plan — an operational audit trail for what moved where and when. FileAgent does not claim formal compliance certifications.",
  },
  {
    q: "Is there a free version?",
    a: freePlan
      ? `Yes. FileAgent ${freePlan.name} automates Local and SMB transfers at no cost with ${limitPhrase(freePlan, "maxUsers")}, ${limitPhrase(freePlan, "maxActiveJobs")}, and ${limitPhrase(freePlan, "maxCredentials")}, including retries, email failure alerts, and configuration backup and restore. Paid plans add more users and active jobs, more connectors, longer history, and support. See the pricing page for details.`
      : "See the pricing page for current plans.",
  },
  {
    q: "What can the FileAgent AI Assistant do?",
    a: `On ${availabilityText("assistant.ai")}, the AI Assistant can help create and edit jobs in plain English, answer questions about FileAgent, explain job types and settings, and recommend appropriate transfer types, schedules, destinations, and reliability controls. It prepares supported changes for review and applies them only after the signed-in user confirms.`,
  },
  {
    q: "Who controls the AI provider and API key?",
    a: "Your organization does. Administrators connect a customer-managed API key for Anthropic Claude, Google Gemini, or OpenAI and manage that provider account, billing, limits, policies, and terms. FileAgent does not bundle or require one AI vendor.",
  },
];

const highestSelfServePlan = selfServePlans[selfServePlans.length - 1];
const aiPlanNames = availabilityText("assistant.ai");

function annualBillingAnswer(): string {
  const totals = selfServePlans
    .map((plan) => `${priceAmount(plan, "annual")} for ${plan.name}`)
    .join(" and ");
  const note = annualToggleNote();
  const comparison = note ? `, the equivalent of ${note} compared with twelve monthly charges` : "";
  return `${definition("annualBilling").text} Annual subscriptions are ${totals}${comparison}. Monthly subscriptions are billed each month.`;
}

const pricingFaq: FaqItem[] = [
  { q: "What counts as an active job?", a: definition("activeJob").text },
  { q: "Who counts as a named console user?", a: definition("namedUser").text },
  { q: "How does annual billing work?", a: annualBillingAnswer() },
  {
    q: "How does licensing work after I buy?",
    a: `After checkout you receive a license key to enter in FileAgent under Settings → Licensing, which applies your plan to that production installation.${
      minimumAppVersion ? ` Licenses for the plans on this page require FileAgent ${minimumAppVersion} or later.` : ""
    }`,
  },
  ...(contactSalesPlan && highestSelfServePlan
    ? [
        {
          q: `What if I need more than ${highestSelfServePlan.name}?`,
          a: `${contactSalesPlan.name} is for organizations that need contracted capacity, agreed retention, or custom commercial terms. Contact sales for a quote; ${contactSalesPlan.name} agreements are invoiced rather than purchased through online checkout.`,
        },
      ]
    : []),
  {
    q: "Does the AI Assistant include an AI provider subscription?",
    a: `No. ${aiPlanNames} include the FileAgent AI Assistant. ${definition("aiProvider").text} Google currently offers a free tier for eligible Gemini API models and qualifying usage; current model availability, rate limits, policies, and terms apply.`,
  },
  {
    q: "My subscription is for a plan that is no longer listed. What changes?",
    a: "Nothing changes automatically. Existing subscriptions and licenses keep the plan, limits, and features they were purchased with and continue to renew on their current terms. Plans that are no longer listed are not available to new customers.",
  },
];

interface CoreSeo extends Omit<SeoMeta, "jsonLd"> {
  jsonLd?: object | object[];
}

const coreSeo: Record<string, CoreSeo> = {
  home: {
    title: "FileAgent | Self-Hosted File Transfer Automation for IT Teams",
    description:
      "Stop relying on mystery scripts for business-critical file transfers. FileAgent is self-hosted file transfer automation: schedule, monitor, and audit transfers across local, SMB, SFTP, FTP, S3, and Azure Blob — without enterprise MFT overhead.",
    path: "/",
    jsonLd: [appSchema(), faqSchema(homeFaq)],
  },
  pricing: {
    title: "Pricing | FileAgent File Transfer Automation",
    description: `Compare FileAgent ${planNameList(plans)}: connectors, scheduling, alerts, security, retention, support, and the FileAgent AI Assistant on ${aiPlanNames}.`,
    path: "/pricing",
    jsonLd: [appSchema(), faqSchema(pricingFaq)],
  },
  download: {
    title: "Download FileAgent for Linux & Windows | FileAgent",
    description: "Download FileAgent for Linux (.deb) or Windows (EXE), install it on your server, and activate paid features with a license key.",
    path: "/download",
  },
  activation: {
    title: "License Activation | FileAgent",
    description: "Retrieve or resend your FileAgent activation email and license key, and continue setup.",
    path: "/licensing/activation",
  },
  support: {
    title: "Support | FileAgent",
    description: "Getting started, deployment, licensing, and support resources for FileAgent. Full documentation at docs.fileagent.app.",
    path: "/support",
  },
  privacy: {
    title: "Privacy Policy | FileAgent",
    description:
      "FileAgent's Privacy Policy: what we collect for the website, purchases, licensing, and support — and why your self-hosted file contents and credentials never reach our systems. Includes Indiana (ICDPA) and U.S. state privacy rights.",
    path: "/privacy",
  },
  terms: {
    title: "Terms of Service | FileAgent",
    description:
      "FileAgent's Terms of Service: license grant, subscriptions and billing, acceptable use, Scheduled Purge and data-loss risk allocation, warranties, liability limits, and governing law. Read before you download or buy.",
    path: "/terms",
  },
  contact: {
    title: "Request Enterprise Pricing | FileAgent",
    description:
      "Contact FileAgent sales for Enterprise: contracted capacity, agreed retention, custom commercial terms, and scoped deployment assistance.",
    path: "/contact-sales",
  },
};

function normalizePath(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

function routeForPath(pathname: string): Route {
  const path = normalizePath(pathname);
  if (path === "/pricing") return "pricing";
  if (path === "/download") return "download";
  if (path === "/licensing/activation") return "activation";
  if (path === "/support" || path === "/docs") return "support";
  if (path === "/privacy") return "privacy";
  if (path === "/terms") return "terms";
  if (path === "/contact-sales" || path === "/contact") return "contact";
  if (landingBySlug[path]) return "landing";
  if (comparisonBySlug[path]) return "comparison";
  return "home";
}

function resolveSeo(pathname: string): SeoMeta {
  const path = normalizePath(pathname);
  const landing = landingBySlug[path];
  if (landing) {
    return { title: landing.seoTitle, description: landing.seoDescription, path: landing.slug, jsonLd: faqSchema(landing.faq) };
  }
  const comparison = comparisonBySlug[path];
  if (comparison) {
    return { title: comparison.seoTitle, description: comparison.seoDescription, path: comparison.slug, jsonLd: faqSchema(comparison.faq) };
  }
  const route = routeForPath(pathname);
  return coreSeo[route] ?? coreSeo.home;
}

type Navigate = (href: string) => (event: React.MouseEvent<HTMLAnchorElement>) => void;

function scrollToHash(hash: string) {
  const id = hash.replace(/^#/, "");
  if (!id) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  const target = document.getElementById(id);
  if (target) {
    target.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

let revealObserver: IntersectionObserver | null = null;

/**
 * Reveal-on-scroll: fade/slide `.reveal` elements into view via
 * IntersectionObserver. Re-runs on route change so each page's elements are
 * observed, and reveals everything immediately when reduced motion is set or
 * IntersectionObserver is unavailable.
 */
function useScrollReveal(key: unknown) {
  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const els = Array.from(document.querySelectorAll<HTMLElement>(".reveal:not(.is-visible)"));
      if (!els.length) return;

      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduce || !("IntersectionObserver" in window)) {
        els.forEach((el) => el.classList.add("is-visible"));
        return;
      }

      const io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add("is-visible");
              obs.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
      );
      els.forEach((el) => io.observe(el));
      revealObserver = io;
    });

    return () => {
      window.cancelAnimationFrame(frame);
      revealObserver?.disconnect();
      revealObserver = null;
    };
  }, [key]);
}

function App() {
  // Key routing on the full pathname so navigating between two pages of the
  // same kind (e.g. landing → landing) still re-renders and refreshes SEO.
  const [path, setPath] = useState<string>(window.location.pathname);
  const [publicConfig, setPublicConfig] = useState<PublicConfig>({});
  const [releaseManifest, setReleaseManifest] = useState<ReleaseManifest | null>(null);
  const route = routeForPath(path);

  useEffect(() => {
    fetchPublicConfig().then(setPublicConfig);
  }, []);

  // Single-source release manifest (latest.json) drives the download buttons.
  useEffect(() => {
    fetchLatestRelease().then(setReleaseManifest);
  }, []);

  useEffect(() => {
    const onPopState = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Deep links such as /pricing#plan-business (used by FileAgent upgrade
  // prompts) target elements React renders after load, so scroll once they
  // exist, and again after web fonts load in case the page reflowed.
  useEffect(() => {
    const id = window.location.hash.replace(/^#/, "");
    if (!id) return;
    let cancelled = false;
    const scrollToTarget = () => {
      if (!cancelled) document.getElementById(id)?.scrollIntoView({ block: "start" });
    };
    const timer = window.setTimeout(() => {
      scrollToTarget();
      void document.fonts?.ready.then(() => window.requestAnimationFrame(scrollToTarget));
    }, 100);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    applySeo(resolveSeo(path));
    trackRoute();
  }, [path]);

  useScrollReveal(path);

  const navigate: Navigate = (href) => (event) => {
    const url = new URL(href, window.location.origin);
    if (url.origin !== window.location.origin) return; // let external/mailto links behave normally
    event.preventDefault();

    const samePage = url.pathname.replace(/\/+$/, "") === window.location.pathname.replace(/\/+$/, "");

    window.history.pushState({}, "", url.pathname + url.search + url.hash);
    setPath(url.pathname);

    if (url.hash) {
      if (samePage) {
        scrollToHash(url.hash);
      } else {
        // Allow the new route to render before scrolling to the anchor.
        window.setTimeout(() => scrollToHash(url.hash), 60);
      }
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <SiteFrame route={route} navigate={navigate}>
      {route === "home" && <HomePage navigate={navigate} publicConfig={publicConfig} />}
      {route === "pricing" && <PricingPage navigate={navigate} publicConfig={publicConfig} />}
      {route === "download" && <DownloadPage navigate={navigate} publicConfig={publicConfig} manifest={releaseManifest} />}
      {route === "activation" && <ActivationPage navigate={navigate} publicConfig={publicConfig} manifest={releaseManifest} />}
      {route === "support" && <SupportPage navigate={navigate} />}
      {route === "privacy" && <PrivacyPage />}
      {route === "terms" && <TermsPage />}
      {route === "contact" && <ContactSalesPage navigate={navigate} />}
      {route === "landing" && <LandingRoute navigate={navigate} path={path} />}
      {route === "comparison" && <ComparisonRoute navigate={navigate} path={path} />}
    </SiteFrame>
  );
}

function LandingRoute({ navigate, path }: { navigate: Navigate; path: string }) {
  const page = landingBySlug[normalizePath(path)];
  if (!page) return <HomePage navigate={navigate} publicConfig={{}} />;
  return <LandingPageView page={page} navigate={navigate} />;
}

function ComparisonRoute({ navigate, path }: { navigate: Navigate; path: string }) {
  const page = comparisonBySlug[normalizePath(path)];
  if (!page) return <HomePage navigate={navigate} publicConfig={{}} />;
  return <ComparisonPageView page={page} navigate={navigate} />;
}

/* ------------------------------------------------------------------ */
/* Brand + chrome                                                      */
/* ------------------------------------------------------------------ */

function BrandLogo() {
  return (
    <img
      className="brand-logo"
      src="/fileagent-brand-full-logo.png?v=20260806"
      alt="FileAgent"
      width={1501}
      height={359}
      loading="eager"
      decoding="async"
      fetchPriority="high"
    />
  );
}

const navLinks: { label: string; href: string }[] = [
  { label: "Product", href: "/#solution" },
  { label: "Features", href: "/#features" },
  { label: "Use Cases", href: "/#use-cases" },
  { label: "Security", href: "/#security" },
  { label: "Pricing", href: "/pricing" },
  { label: "Docs", href: siteConfig.docsUrl },
  { label: "Support", href: "/support" },
];

function SiteFrame({ children, route, navigate }: { children: React.ReactNode; route: Route; navigate: Navigate }) {
  return (
    <>
      <NavBar route={route} navigate={navigate} />
      <main>{children}</main>
      <Footer navigate={navigate} />
    </>
  );
}

function NavBar({ route, navigate }: { route: Route; navigate: Navigate }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("nav-open", menuOpen);
    return () => document.body.classList.remove("nav-open");
  }, [menuOpen]);

  const handle = (href: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    setMenuOpen(false);
    navigate(href)(event);
  };

  const isActive = (href: string) =>
    (href === "/pricing" && route === "pricing") || (href === "/support" && route === "support");

  return (
    <>
      <header className={`topbar ${scrolled ? "is-scrolled" : ""}`}>
        <div className="topbar-inner shell">
          <a className="brand" href="/" onClick={handle("/")} aria-label="FileAgent home">
            <BrandLogo />
          </a>

          <nav className="nav-desktop" aria-label="Primary">
            {navLinks.map((link) => (
              <a key={link.label} className={isActive(link.href) ? "active" : ""} href={link.href} onClick={handle(link.href)}>
                {link.label}
              </a>
            ))}
          </nav>

          <div className="topbar-actions">
            <a className="topbar-secondary" href="/contact-sales" onClick={handle("/contact-sales")}>
              Contact sales
            </a>
            <a className="button button-primary topbar-cta" href="/download" onClick={handle("/download")}>
              <Download size={17} /> Get FileAgent
            </a>
            <button
              type="button"
              className="nav-toggle"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Rendered as a sibling of <header> (not a child) so the topbar's
          backdrop-filter never becomes the containing block for this fixed
          overlay — otherwise the scrolled menu positions to the 72px topbar. */}
      <div className={`nav-mobile ${menuOpen ? "open" : ""}`} aria-hidden={!menuOpen}>
        <nav aria-label="Mobile">
          {navLinks.map((link) => (
            <a key={link.label} href={link.href} onClick={handle(link.href)}>
              {link.label}
              <ChevronRight size={18} />
            </a>
          ))}
        </nav>
        <div className="nav-mobile-actions">
          <a className="button button-secondary" href="/pricing" onClick={handle("/pricing")}>
            View pricing
          </a>
          <a className="button button-primary" href="/download" onClick={handle("/download")}>
            <Download size={18} /> Get FileAgent
          </a>
        </div>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Home page                                                           */
/* ------------------------------------------------------------------ */

function HomePage({ navigate, publicConfig }: { navigate: Navigate; publicConfig: PublicConfig }) {
  return (
    <>
      <Hero navigate={navigate} />
      <ConnectorStrip />
      <StatBand />
      <ProblemSection />
      <SolutionSection />
      <FeatureSection />
      <AiAssistantSection navigate={navigate} />
      <HowItWorks />
      <UseCases />
      <Differentiation />
      <SecuritySection />
      <PricingPreview navigate={navigate} publicConfig={publicConfig} />
      <HomeFaq />
      <CtaBand navigate={navigate} />
    </>
  );
}

function Hero({ navigate }: { navigate: Navigate }) {
  return (
    <section className="hero">
      <div className="shell hero-inner">
        <div className="hero-copy reveal">
          <p className="eyebrow eyebrow-pill">
            <ShieldCheck size={15} /> Self-hosted file transfer automation
          </p>
          <h1>
            Automate the file transfers your business runs on — <span className="grad-text">without the fragile scripts.</span>
          </h1>
          <p className="lead">
            FileAgent is one self-hosted console to schedule, run, and audit every transfer across local storage, SMB, SFTP,
            FTP, AWS S3, and Azure Blob. The control of enterprise managed file transfer — without the cost, the complexity, or
            the scripts no one wants to own.
          </p>
          <div className="hero-actions">
            <a className="button button-primary button-lg" href="/download" onClick={navigate("/download")}>
              <Download size={18} /> Download free
            </a>
            <a className="button button-secondary button-lg" href="/pricing" onClick={navigate("/pricing")}>
              Compare plans <ArrowRight size={18} />
            </a>
          </div>
          <ul className="hero-proof" aria-label="What you get">
            <li>
              <BadgeCheck size={16} /> Self-hosted &amp; auditable
            </li>
            <li>
              <BadgeCheck size={16} /> Scheduling, retries &amp; alerts
            </li>
            <li>
              <BadgeCheck size={16} /> Central, encrypted credentials
            </li>
          </ul>
        </div>

        <div className="hero-media reveal" data-delay="2">
          <div className="app-window">
            <span className="app-window-bar" aria-hidden="true">
              <span className="win-dots">
                <i />
                <i />
                <i />
              </span>
              <span className="app-window-title">
                <LayoutDashboard size={13} /> FileAgent — Operations dashboard
              </span>
            </span>
            <div className="app-window-screen">
              <img
                src="/fileagent-hero-v2.png?v=20260806"
                alt="FileAgent dashboard showing transfer metrics, connector usage, reliability, and busiest paths"
                loading="eager"
              />
            </div>
          </div>
          <div className="hero-chip tl" aria-hidden="true">
            <span className="hero-chip-icon">
              <CheckCircle2 size={16} />
            </span>
            <span>
              <strong>Every run logged</strong>
              <small>scheduled · retried · alertable</small>
            </span>
          </div>
          <div className="hero-chip br" aria-hidden="true">
            <span className="hero-chip-icon">
              <CalendarClock size={16} />
            </span>
            <span>
              <strong>Next run · 02:00</strong>
              <small>SFTP → S3 · nightly</small>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

const connectors = ["Local folders", "SMB shares", "FTP", "SFTP", "AWS S3", "Azure Blob"];

function ConnectorStrip() {
  return (
    <section className="connector-strip-band" aria-label="Supported systems">
      <div className="shell connector-strip-inner">
        <span className="connector-strip-label">Connects the systems you already run</span>
        <ul className="connector-logos">
          {connectors.map((name) => (
            <li key={name}>
              <Server size={16} aria-hidden="true" /> {name}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const stats: { num: React.ReactNode; label: string }[] = [
  { num: <>6</>, label: "Storage connectors — Local, SMB, FTP, SFTP, S3 & Azure Blob" },
  { num: <>4</>, label: "File operations — Copy, Move, Sync/Mirror & Scheduled Purge" },
  { num: <>AES&#8209;256</>, label: "GCM-encrypted credentials, separate from job definitions" },
  {
    num: <>{Math.max(...countLimitValues("logRetentionDays"))}<small>-day</small></>,
    label: `Log and run-history retention on ${
      plans.find((plan) => plan.limits.logRetentionDays?.value === Math.max(...countLimitValues("logRetentionDays")))?.name
    }${contactSalesPlan ? `; ${contactSalesPlan.name} retention by agreement` : ""}`,
  },
];

function StatBand() {
  return (
    <section className="section-block stat-band shell">
      <div className="stat-grid">
        {stats.map((stat, i) => (
          <article className="stat reveal" data-delay={(i % 4) as 0 | 1 | 2 | 3} key={stat.label}>
            <div className="stat-num">{stat.num}</div>
            <p className="stat-label">{stat.label}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

const problems: { icon: React.ReactNode; title: string; body: string }[] = [
  {
    icon: <Terminal />,
    title: "Scripts nobody wants to own",
    body: "PowerShell and Bash jobs grow organically, break silently, and leave with the person who wrote them.",
  },
  {
    icon: <CalendarClock />,
    title: "Cron & Task Scheduler blind spots",
    body: "Scheduled tasks run somewhere on a server with no shared visibility into what ran, what failed, or why.",
  },
  {
    icon: <Network />,
    title: "FTP clients can't operate",
    body: "Manual FTP/SFTP tools move files, but offer no scheduling, retries, history, or alerting for recurring work.",
  },
  {
    icon: <AlertTriangle />,
    title: "Failures discovered too late",
    body: "Without retries and alerts, a missed transfer surfaces as a downstream problem hours or days later.",
  },
  {
    icon: <KeyRound />,
    title: "Credentials scattered everywhere",
    body: "Passwords and keys end up hardcoded in scripts, config files, and shared docs with no central control.",
  },
  {
    icon: <Building2 />,
    title: "Enterprise MFT is overkill",
    body: "Traditional managed file transfer platforms are powerful, but expensive, heavy, and slow to roll out.",
  },
];

function ProblemSection() {
  return (
    <section className="section-block shell" id="problem">
      <div className="section-heading center reveal">
        <p className="eyebrow">The problem</p>
        <h2>Moving files by hand doesn't scale.</h2>
        <p>
          Most teams run business-critical transfers on a patchwork of scripts, scheduled tasks, and FTP clients. It holds up
          right until it doesn't — and when it breaks, no one can see what failed or why.
        </p>
      </div>
      <div className="problem-grid">
        {problems.map((item, i) => (
          <article className="problem-card reveal" data-delay={(i % 3) as 0 | 1 | 2} key={item.title}>
            <span className="problem-icon">{item.icon}</span>
            <h3>{item.title}</h3>
            <p>{item.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

const solutionPoints = [
  "Build transfer jobs in a web UI — no scripting required",
  "Connect local, SMB, FTP/SFTP, S3, and Azure Blob endpoints",
  "Schedule jobs and monitor every run in one place",
  "Review logs, retries, and outcomes with full history",
  "Archive moved files and restore when you need them",
  "Configure email, Slack, and Teams alerts on failures",
];

function SolutionSection() {
  return (
    <section className="band" id="solution">
      <div className="shell solution-inner">
        <div className="solution-copy reveal">
          <p className="eyebrow">The solution</p>
          <h2>One control plane for every file transfer.</h2>
          <p className="lead">
            FileAgent replaces the patchwork. Define what moves, where it goes, and when — then get the scheduling, visibility,
            and control that scripts and FTP clients never gave you. No code to maintain, no blind spots.
          </p>
          <ul className="solution-list">
            {solutionPoints.map((point) => (
              <li key={point}>
                <span className="tick">
                  <Check size={15} />
                </span>
                {point}
              </li>
            ))}
          </ul>
        </div>
        <div className="reveal" data-delay="2">
          <ArchitectureFlow />
        </div>
      </div>
    </section>
  );
}

const archConnectors: { name: string; icon: React.ReactNode }[] = [
  { name: "Local", icon: <Folder size={15} /> },
  { name: "SMB", icon: <Server size={15} /> },
  { name: "FTP", icon: <Globe size={15} /> },
  { name: "SFTP", icon: <Network size={15} /> },
  { name: "AWS S3", icon: <Cloud size={15} /> },
  { name: "Azure Blob", icon: <Database size={15} /> },
];

function ArchZone({ label }: { label: string }) {
  return (
    <div className="arch-zone">
      <span className="arch-zone-label">{label}</span>
      <div className="arch-nodes">
        {archConnectors.map((c) => (
          <span className="arch-node" key={c.name}>
            <span className="arch-node-icon">{c.icon}</span>
            {c.name}
          </span>
        ))}
      </div>
    </div>
  );
}

function ArchRail() {
  return (
    <span className="arch-rail" aria-hidden="true">
      <span className="arch-pulse" />
      <span className="arch-pulse" />
      <span className="arch-pulse" />
    </span>
  );
}

function ArchitectureFlow() {
  return (
    <div
      className="arch-flow"
      role="img"
      aria-label="FileAgent pulls files from source systems — local folders, SMB shares, FTP, SFTP, AWS S3, and Azure Blob — runs them through a scheduled engine that performs copy, move, and Sync/Mirror jobs with logging and alerts, then delivers them to the same set of destination systems."
    >
      <ArchZone label="Sources" />
      <ArchRail />
      <div className="arch-engine">
        <div className="arch-engine-head">
          <span className="icon-badge">
            <Workflow />
          </span>
          <span className="arch-engine-name">
            <strong>FileAgent</strong>
            <small>Scheduled engine</small>
          </span>
        </div>
        <div className="arch-ops">
          <span>Copy</span>
          <span>Move</span>
          <span>Sync/Mirror</span>
        </div>
        <div className="arch-engine-meta">
          <span>
            <ScrollText size={13} /> Logs
          </span>
          <span>
            <Bell size={13} /> Alerts
          </span>
          <span>
            <RefreshCw size={13} /> Retries
          </span>
        </div>
      </div>
      <ArchRail />
      <ArchZone label="Destinations" />
    </div>
  );
}

const features: { icon: React.ReactNode; title: string; body: string; className: string; tag?: string }[] = [
  {
    icon: <CalendarClock />,
    title: "Scheduled transfers",
    body: "Create recurring jobs with interval, daily, weekly, and monthly calendar schedules — and see the next run time on every job, with no separate cron or Task Scheduler to manage.",
    className: "feature",
    tag: `Calendar schedules · ${minimumPlanTag("jobs.advancedScheduling")}`,
  },
  {
    icon: <ArrowLeftRight />,
    title: "Copy, move & Sync/Mirror",
    body: "Pick the right operation for each job — copy, move, or Sync/Mirror to keep a destination in step with the source.",
    className: "wide",
    tag: `Sync/Mirror · ${minimumPlanTag("jobs.syncMirror")}`,
  },
  {
    icon: <ServerCog />,
    title: "Broad connectors",
    body: "Local, SMB, FTP, SFTP, AWS S3, and Azure Blob — one workflow for all of them, based on your plan.",
    className: "",
  },
  {
    icon: <Trash2 />,
    title: "Scheduled Purge",
    body: "Delete files older than a configured age, by pattern, on a schedule. Permanent deletion — not a backup.",
    className: "",
    tag: minimumPlanTag("jobs.scheduledPurge"),
  },
  {
    icon: <RefreshCw />,
    title: "Retry, conflict & timeout controls",
    body: "Absorb transient SFTP/SMB failures and decide how existing files are handled — fewer silent misses.",
    className: "",
  },
  {
    icon: <ScrollText />,
    title: "Logs & run history",
    body: "Every run is recorded with outcome, file counts, sizes, and per-file detail, retained by plan.",
    className: "",
  },
  {
    icon: <Bell />,
    title: "Email, Slack & Teams alerts",
    body: `Get notified the moment a job fails — by email on ${availabilityText("alerts.emailFailure")}, or in Slack and Teams on ${availabilityFrom("alerts.slack")}.`,
    className: "",
  },
  {
    icon: <Shield />,
    title: "RBAC, SSO, LDAP & IP allowlist",
    body: `Job-scoped roles, SAML 2.0 SSO, LDAP sign-in, IP allowlisting, and session controls on ${availabilityText("security.sso")}.`,
    className: "",
    tag: minimumPlanTag("security.sso"),
  },
  {
    icon: <MessageSquareText />,
    title: "FileAgent AI Assistant",
    body: "Create and edit jobs in plain English, get contextual answers about FileAgent, and receive practical recommendations for transfer workflows using your own supported AI provider.",
    className: "assistant-feature",
    tag: plansWithFeature("assistant.ai").map((plan) => plan.name).join(" + "),
  },
];

function FeatureSection() {
  return (
    <section className="section-block shell" id="features">
      <div className="section-heading center reveal">
        <p className="eyebrow">Features</p>
        <h2>Everything recurring file movement actually needs.</h2>
        <p>
          A focused, practical toolset for the workflows teams quietly depend on: scheduling, traceability, encrypted
          credentials, and proactive alerts — without a single line of script to maintain.
        </p>
      </div>
      <div className="bento">
        {features.map((feature, i) => (
          <article className={`bento-item ${feature.className} reveal`} data-delay={(i % 4) as 0 | 1 | 2 | 3} key={feature.title}>
            <span className="icon-badge">{feature.icon}</span>
            <h3>{feature.title}</h3>
            <p>{feature.body}</p>
            {feature.tag && <span className="bento-tag">{feature.tag}</span>}
          </article>
        ))}
      </div>
    </section>
  );
}

const assistantPrompts = [
  "Create a nightly copy job from this local folder to an SMB share.",
  "Change this job to run every weekday at 10:00 PM.",
  "Which transfer type should I use to keep these folders synchronized?",
  "How should I configure this job to reduce the risk of interrupted transfers?",
];

function AiAssistantSection({ navigate }: { navigate: Navigate }) {
  return (
    <section className="band band-soft ai-assistant-section" id="ai-assistant">
      <div className="shell ai-assistant-layout">
        <div className="solution-copy reveal">
          <p className="eyebrow">FileAgent AI Assistant</p>
          <h2>Operate FileAgent in plain English.</h2>
          <p className="lead">
            Create and edit transfer jobs, get immediate answers about FileAgent, and receive practical workflow
            recommendations without digging through settings or documentation.
          </p>
          <p className="ai-plan-note">
            <BadgeCheck size={17} /> Included with {aiPlanNames}
          </p>
          <ul className="solution-list">
            <li>
              <span className="tick"><Check size={15} /></span>
              Describe the workflow you need and review the proposed source, destination, schedule, and settings.
            </li>
            <li>
              <span className="tick"><Check size={15} /></span>
              Get contextual help with job types, transfer modes, destinations, schedules, and reliability controls.
            </li>
            <li>
              <span className="tick"><Check size={15} /></span>
              Apply only supported actions within the signed-in user&apos;s permissions, after explicit confirmation.
            </li>
          </ul>
          <div className="ai-assistant-actions">
            <a
              className="button button-primary"
              href={`/pricing#${planAnchor(minimumPlanFor("assistant.ai"))}`}
              onClick={navigate(`/pricing#${planAnchor(minimumPlanFor("assistant.ai"))}`)}
            >
              Explore {minimumPlanFor("assistant.ai").name} <ArrowRight size={17} />
            </a>
            <a className="button button-secondary" href="/contact-sales" onClick={navigate("/contact-sales")}>
              See Enterprise options
            </a>
          </div>
        </div>

        <div className="ai-assistant-panel reveal" data-delay="2">
          <div className="ai-panel-head">
            <span className="icon-badge"><MessageSquareText /></span>
            <div>
              <h3>Ask FileAgent</h3>
              <p>Concrete requests, grounded in your FileAgent environment.</p>
            </div>
          </div>
          <ul className="ai-prompt-list" aria-label="Example AI Assistant requests">
            {assistantPrompts.map((prompt) => (
              <li key={prompt}>&ldquo;{prompt}&rdquo;</li>
            ))}
          </ul>
          <div className="ai-provider-block">
            <h3>Your provider. Your API key. Your control.</h3>
            <p>
              Connect your organization&apos;s own Anthropic Claude, Google Gemini, or OpenAI API key. Administrators choose
              the provider and manage its account, usage, billing, limits, and terms. FileAgent stores the configured key in
              protected credential storage.
            </p>
          </div>
          <div className="ai-gemini-note">
            <strong>Start with Gemini&apos;s available free tier</strong>
            <span>
              Eligible Gemini API models can currently be used through Google&apos;s free tier. Model availability, rate limits,
              provider policies, and terms apply.
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

const steps: { title: string; body: string }[] = [
  {
    title: "Install on your host",
    body: "Run the Linux .deb or Windows EXE on a server or workstation that can reach your source and destination systems.",
  },
  {
    title: "Store your credentials",
    body: "Add SMB, SFTP, FTP, S3, and Azure connections once. They're encrypted, testable, and reused across every job.",
  },
  {
    title: "Build & schedule jobs",
    body: "Define source → destination → mode with filters, retries, and a schedule — all in the web UI, no scripting.",
  },
  {
    title: "Monitor & get alerted",
    body: "Watch every run on the dashboard and get email, Slack, or Teams alerts the moment a transfer fails.",
  },
];

function HowItWorks() {
  return (
    <section className="band band-soft" id="how-it-works">
      <div className="shell">
        <div className="section-heading center reveal">
          <p className="eyebrow">How it works</p>
          <h2>From install to automated in four steps.</h2>
          <p>Stand FileAgent up on one host and turn a fragile, manual transfer into a managed job in minutes.</p>
        </div>
        <div className="steps-flow">
          {steps.map((step, i) => (
            <article className="step-card reveal" data-delay={(i % 4) as 0 | 1 | 2 | 3} key={step.title}>
              <span className="step-card-num">{i + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const useCases: { icon: React.ReactNode; title: string; body: string }[] = [
  { icon: <FileText />, title: "Nightly reporting transfers", body: "Deliver report exports to partners, BI tools, and shared folders on a dependable schedule." },
  { icon: <Cloud />, title: "SMB shares to cloud storage", body: "Move files from Windows network shares to S3 or Azure Blob without manual uploads." },
  { icon: <Network />, title: "Vendor & customer SFTP", body: "Automate recurring partner, vendor, and legacy SFTP/FTP exchange workflows." },
  { icon: <Terminal />, title: "Replace brittle scripts", body: "Turn fragile PowerShell and Bash jobs into visible, managed, schedulable workflows." },
  { icon: <FolderSync />, title: "Department file automation", body: "Give teams repeatable handoffs between departments, servers, and staging folders." },
  { icon: <Archive />, title: "Backup & archive flows", body: "Mirror important folders and archive recurring exports with cleaner operational history." },
  { icon: <Trash2 />, title: "Stale file cleanup", body: "Purge old exports, processed files, and logs on a schedule so retention folders stop growing forever." },
  { icon: <Building2 />, title: "Multi-location operations", body: "Coordinate file movement across sites, branches, and regions from one console." },
  { icon: <Gauge />, title: "MSP-managed workflows", body: "Run dependable, auditable file workflows on behalf of multiple clients." },
];

function UseCases() {
  return (
    <section className="section-block" id="use-cases">
      <div className="shell use-case-layout">
        <div className="section-heading sticky-heading reveal">
          <p className="eyebrow">Use cases</p>
          <h2>Built for the workflows you run every day.</h2>
          <p>Wherever recurring file movement needs to be predictable, visible, and maintainable, FileAgent fits.</p>
          <a className="button button-secondary" href="/pricing">
            Find your plan <ArrowRight size={17} />
          </a>
        </div>
        <div className="use-case-grid">
          {useCases.map((item, i) => (
            <article className="use-case reveal" data-delay={(i % 2) as 0 | 1} key={item.title}>
              <span className="use-case-icon">{item.icon}</span>
              <div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const comparisons: { tool: string; gap: string; fileagent: string }[] = [
  {
    tool: "Manual FTP/SFTP clients",
    gap: "Move files by hand with no scheduling, retries, or history.",
    fileagent: "Scheduled, repeatable jobs with retries, logs, and alerts.",
  },
  {
    tool: "Cloud storage sync tools",
    gap: "Sync a folder to one vendor, but ignore SMB, FTP/SFTP, and operations.",
    fileagent: "Many source and destination types with operational control.",
  },
  {
    tool: "Backup tools",
    gap: "Built for recovery points, not routine file delivery between systems.",
    fileagent: "Purpose-built for moving and delivering files on a schedule.",
  },
  {
    tool: "Scripts & scheduled tasks",
    gap: "Powerful but fragile, undocumented, and hard to monitor or hand off.",
    fileagent: "A managed web UI with shared visibility and access control.",
  },
  {
    tool: "Enterprise MFT platforms",
    gap: "Capable, but expensive, complex, and slow to deploy for most teams.",
    fileagent: "Practical controls and pricing that scale from solo to enterprise.",
  },
];

function Differentiation() {
  return (
    <section className="section-block shell" id="compare">
      <div className="section-heading center reveal">
        <p className="eyebrow">Where FileAgent fits</p>
        <h2>The middle ground between scripts and heavy MFT.</h2>
        <p>
          FileAgent gives you operational control over file movement without the maintenance burden of scripts or the cost and
          complexity of traditional enterprise platforms.
        </p>
      </div>
      <div className="compare-table-wrap reveal">
        <table className="compare-table">
          <thead>
            <tr>
              <th>Approach</th>
              <th>Common gap</th>
              <th>With FileAgent</th>
            </tr>
          </thead>
          <tbody>
            {comparisons.map((row) => (
              <tr key={row.tool}>
                <td>
                  <strong>{row.tool}</strong>
                </td>
                <td className="compare-gap">{row.gap}</td>
                <td className="compare-win">
                  <Check size={16} aria-hidden="true" /> {row.fileagent}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const securityItems: { icon: React.ReactNode; title: string; body: string }[] = [
  { icon: <Server />, title: "Runs in your environment", body: "FileAgent installs on a host you control, close to the source and destination systems it moves files between." },
  { icon: <Lock />, title: "Encrypted credentials & secure protocols", body: "Connection credentials are encrypted in FileAgent's store, and you can move data over encrypted protocols like SFTP, FTPS, and HTTPS — keeping the sensitive parts of every workflow protected." },
  { icon: <History />, title: "Job logs & history", body: "Every run is recorded with outcomes and detail, with retention configurable by plan." },
  { icon: <Users />, title: "Role-based access", body: `${availabilityText("job_scoped_rbac")} add job-scoped RBAC and archive permissions to control who can do what.` },
  {
    icon: <TimerReset />,
    title: "Retention controls",
    body: `Keep log and run history for ${Math.min(...countLimitValues("logRetentionDays"))} to ${Math.max(...countLimitValues("logRetentionDays"))} days depending on plan${
      contactSalesPlan ? `, or set retention in an ${contactSalesPlan.name} agreement` : ""
    }.`,
  },
  { icon: <ShieldCheck />, title: "Identity & access controls", body: `SSO, LDAP, IP allowlists, and session security are available on ${availabilityText("security.sso")}.` },
  { icon: <MessageSquareText />, title: "Customer-controlled AI provider", body: "AI Assistant administrators choose Claude, Gemini, or OpenAI, supply the API key, and manage the provider account, usage, billing, limits, and terms." },
];

function SecuritySection() {
  return (
    <section className="band" id="security">
      <div className="shell">
        <div className="section-heading center reveal">
          <p className="eyebrow">Security &amp; trust</p>
          <h2>Designed for controlled, auditable file operations.</h2>
          <p>
            FileAgent is built to run inside your infrastructure with the visibility and access controls operations teams expect.
          </p>
        </div>
        <div className="security-grid">
          {securityItems.map((item, i) => (
            <article className="security-card reveal" data-delay={(i % 3) as 0 | 1 | 2} key={item.title}>
              <span className="icon-badge">{item.icon}</span>
              <div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            </article>
          ))}
        </div>
        <div className="center">
          <a className="button button-secondary" href={siteConfig.docsUrl}>
            Read the docs <ArrowRight size={17} />
          </a>
        </div>
      </div>
    </section>
  );
}

function HomeFaq() {
  return (
    <section className="section-block shell" id="faq">
      <div className="section-heading center reveal">
        <p className="eyebrow">FAQ</p>
        <h2>File transfer automation, answered</h2>
        <p>The questions IT teams ask before standardizing their file transfers on FileAgent.</p>
      </div>
      <div className="faq-accordion reveal">
        {homeFaq.map((item, i) => (
          <details className="faq-item" key={item.q} open={i === 0}>
            <summary>
              {item.q}
              <ChevronDown className="faq-chevron" size={20} aria-hidden="true" />
            </summary>
            <div className="faq-item-body">{item.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Pricing                                                             */
/* ------------------------------------------------------------------ */

function BillingToggle({ interval, onChange }: { interval: BillingInterval; onChange: (next: BillingInterval) => void }) {
  const annualNote = annualToggleNote();
  return (
    <div className="billing-switch" role="group" aria-label="Billing interval">
      <button type="button" className={interval === "monthly" ? "active" : ""} aria-pressed={interval === "monthly"} onClick={() => { onChange("monthly"); if (interval !== "monthly") track("billing_interval_changed", { interval: "monthly" }); }}>
        Monthly
      </button>
      <button type="button" className={interval === "annual" ? "active" : ""} aria-pressed={interval === "annual"} onClick={() => { onChange("annual"); if (interval !== "annual") track("billing_interval_changed", { interval: "annual" }); }}>
        Annual {annualNote && <span className="save-pill">{annualNote}</span>}
      </button>
    </div>
  );
}

function planHref(plan: Plan, interval: BillingInterval, publicConfig: PublicConfig): string {
  if (plan.purchase === "download") return "/download";
  if (plan.purchase === "contact_sales") return "/contact-sales";
  return checkoutUrl(publicConfig, plan.id, interval);
}

function planCtaLabel(plan: Plan): string {
  if (plan.purchase === "download") return "Download free";
  if (plan.purchase === "contact_sales") return "Contact sales";
  return `Start with ${plan.name}`;
}

function PlanCtaIcon({ plan }: { plan: Plan }) {
  if (plan.purchase === "download") return <Download size={16} aria-hidden="true" />;
  if (plan.purchase === "contact_sales") return <Mail size={16} aria-hidden="true" />;
  return <ArrowRight size={16} aria-hidden="true" />;
}

function PlanCard({
  plan,
  interval,
  publicConfig,
  navigate,
}: {
  plan: Plan;
  interval: BillingInterval;
  publicConfig: PublicConfig;
  navigate: Navigate;
}) {
  const href = planHref(plan, interval, publicConfig);
  const external = href.startsWith("http") || href.startsWith("mailto:");
  const featured = Boolean(plan.recommendation);
  const unit = priceUnit(plan, interval);
  const savings = interval === "annual" ? annualSavingsNote(plan) : null;
  const includes = plan.includesPlan ? planById(plan.includesPlan) : null;
  const anchor = planAnchor(plan);

  return (
    <article data-fileagent-plan={plan.id} data-fileagent-interval={interval} className={`plan-card ${featured ? "featured" : ""} reveal`} id={anchor} aria-labelledby={`${anchor}-name`}>
      {plan.recommendation && <span className="plan-badge featured">{plan.recommendation}</span>}
      <div className="plan-head">
        <h3 id={`${anchor}-name`}>{plan.name}</h3>
        <p>{plan.tagline}</p>
      </div>

      <div className="plan-price">
        <span className="plan-amount">{priceAmount(plan, interval)}</span>
        {unit && <span className="plan-unit">{unit}</span>}
      </div>
      <p className="plan-price-sub">{billingNote(plan, interval)}</p>
      <p className="plan-price-save">{savings ?? ""}</p>

      <a
        className={`button ${featured ? "button-primary" : "button-secondary"} plan-cta`}
        href={href}
        onClick={external ? undefined : navigate(href)}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener" : undefined}
      >
        {planCtaLabel(plan)}
        <PlanCtaIcon plan={plan} />
      </a>

      {includes && <p className="plan-includes">Everything in {includes.name}, plus</p>}
      <ul className="plan-list">
        {plan.highlights.map((item) => (
          <li key={item}>
            <Check size={16} aria-hidden="true" />{" "}
            <span>
              {item.includes(" · ")
                ? // A limits line ("3 users · 25 active jobs · …"): keep each limit together when it wraps.
                  item.split(" · ").map((part, index, parts) => (
                    <React.Fragment key={part}>
                      {index > 0 && " "}
                      <span className="nowrap">
                        {part}
                        {index < parts.length - 1 ? " ·" : ""}
                      </span>
                    </React.Fragment>
                  ))
                : item}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function PricingPreview({ navigate, publicConfig }: { navigate: Navigate; publicConfig: PublicConfig }) {
  const [interval, setInterval] = useState<BillingInterval>("annual");

  return (
    <section className="section-block" id="pricing-preview">
      <div className="shell">
        <div className="section-heading center reveal">
          <p className="eyebrow">Pricing</p>
          <h2>Start free. Scale the workflows that matter.</h2>
          <p>Transparent plans from a single server to enterprise operations. No per-transfer billing.</p>
          <BillingToggle interval={interval} onChange={setInterval} />
        </div>
      </div>
      <div className="shell-wide">
        <div className="plan-grid plan-grid-4">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} interval={interval} publicConfig={publicConfig} navigate={navigate} />
          ))}
        </div>
      </div>
      <div className="shell center pricing-preview-cta">
        <a className="button button-secondary" href="/pricing" onClick={navigate("/pricing")}>
          Compare plans in detail <ArrowRight size={17} />
        </a>
      </div>
    </section>
  );
}

const everyPlanFeatureIds = ["jobs.basicScheduling", "alerts.emailFailure", "config.backupRestore", "security.encryptionHttps"];

function PricingPage({ navigate, publicConfig }: { navigate: Navigate; publicConfig: PublicConfig }) {
  const [interval, setInterval] = useState<BillingInterval>("annual");
  const everyPlan = everyPlanFeatureIds.filter((id) => availabilityText(id) === "every plan").map((id) => midSentence(featureLabel(id)));

  return (
    <section className="page-section">
      <div className="shell">
        <div className="section-heading center pricing-hero" style={{ paddingTop: "calc(var(--nav-h) + 24px)" }}>
          <p className="eyebrow">Pricing</p>
          <h1>Plans that scale from one admin to the enterprise.</h1>
          <p>
            Every plan includes the FileAgent web console{everyPlan.length ? `, ${everyPlan.slice(0, -1).join(", ")}, and ${everyPlan[everyPlan.length - 1]}` : ""}.{" "}
            {aiPlanNames} add the FileAgent AI Assistant, which uses your organization&apos;s own supported AI provider key.
          </p>
          <BillingToggle interval={interval} onChange={setInterval} />
        </div>
      </div>

      <div className="shell-wide">
        <div className="plan-grid plan-grid-4">
          {plans.map((plan) => (
            <PlanCard key={plan.id} plan={plan} interval={interval} publicConfig={publicConfig} navigate={navigate} />
          ))}
        </div>
      </div>

      <div className="shell">
        <AiPricingCallout navigate={navigate} />
        <ComparisonMatrix interval={interval} publicConfig={publicConfig} navigate={navigate} />
        <PlanTerms />
        <PricingFaq />
      </div>
      <CtaBand navigate={navigate} />
    </section>
  );
}

function AiPricingCallout({ navigate }: { navigate: Navigate }) {
  const entryPlan = minimumPlanFor("assistant.ai");
  const href = `/pricing#${planAnchor(entryPlan)}`;
  return (
    <section className="ai-pricing-callout" aria-labelledby="ai-pricing-heading">
      <div className="ai-pricing-icon"><MessageSquareText size={24} /></div>
      <div>
        <p className="eyebrow">Included with {aiPlanNames}</p>
        <h2 id="ai-pricing-heading">AI-assisted administration for complex transfer operations</h2>
        <p>
          {aiPlanNames} include the FileAgent AI Assistant for job creation, editing, product guidance, and configuration
          recommendations. Connect a customer-managed Claude, Gemini, or OpenAI API key and keep control of the provider
          account, usage, billing, and provider-level policies.
        </p>
        <p className="ai-pricing-gemini">
          <strong>Compatible with Gemini&apos;s available free API tier.</strong> Eligible models and qualifying usage only;
          Google&apos;s current rate limits and terms apply.
        </p>
      </div>
      <a className="button button-secondary" href={href} onClick={navigate(href)}>
        View {entryPlan.name} <ArrowRight size={16} />
      </a>
    </section>
  );
}

function MatrixCell({ row, cell }: { row: CatalogRow; cell: CatalogCell }) {
  const standardFeatureCell =
    row.kind === "features" && typeof cell.value === "boolean" && cell.display === (cell.value ? "Included" : "Not included");
  if (standardFeatureCell && cell.value) {
    return (
      <span className="cell-yes">
        <Check size={17} aria-hidden="true" />
        <span className="sr-only">Included</span>
      </span>
    );
  }
  if (standardFeatureCell) {
    return (
      <span className="cell-no">
        <span className="cell-dash" aria-hidden="true" />
        <span className="sr-only">Not included</span>
      </span>
    );
  }
  return <span className="cell-text">{cell.display}</span>;
}

function matrixCtaLabel(plan: Plan): string {
  if (plan.purchase === "download") return "Start free";
  if (plan.purchase === "contact_sales") return "Contact sales";
  return `Choose ${plan.name}`;
}

function ComparisonMatrix({
  interval,
  publicConfig,
  navigate,
}: {
  interval: BillingInterval;
  publicConfig: PublicConfig;
  navigate: Navigate;
}) {
  return (
    <section className="comparison-section" aria-labelledby="comparison-heading" id="compare-plans">
      <div className="section-heading center">
        <p className="eyebrow">Compare every plan</p>
        <h2 id="comparison-heading">Full feature &amp; limit breakdown</h2>
      </div>

      <p className="matrix-scroll-hint" aria-hidden="true">
        <ArrowLeftRight size={15} /> Swipe the table sideways to compare all {plans.length} plans
      </p>
      <div className="matrix-scroll" role="region" aria-labelledby="comparison-heading" tabIndex={0}>
        <table className="matrix-table">
          <caption className="sr-only">
            Features and limits for {planNameList(plans)}. Prices in the column headings are{" "}
            {interval === "annual" ? "annual subscription totals" : "monthly subscription charges"}.
          </caption>
          <thead>
            <tr>
              <th className="matrix-row-head" scope="col">
                <span className="matrix-corner">Feature / limit</span>
              </th>
              {plans.map((plan) => (
                <th key={plan.id} scope="col" className={plan.recommendation ? "is-featured" : ""}>
                  <span className="matrix-plan-name">{plan.name}</span>
                  <span className="matrix-plan-price">{compactPrice(plan, interval)}</span>
                </th>
              ))}
            </tr>
          </thead>
          {comparisonGroups.map((group) => (
            <tbody key={group.id}>
              <tr className="matrix-group-row">
                <th scope="rowgroup" colSpan={plans.length + 1}>
                  <span className="matrix-group-title">{group.title}</span>
                </th>
              </tr>
              {group.rows.map((row) => (
                <tr key={row.id}>
                  <th scope="row" className="matrix-row-head">
                    {row.label}
                    {row.hint && <span className="matrix-row-hint">{row.hint}</span>}
                  </th>
                  {plans.map((plan) => (
                    <td key={plan.id} className={plan.recommendation ? "is-featured" : ""} data-plan={plan.name}>
                      <MatrixCell row={row} cell={row.cells[plan.id]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          ))}
          <tbody>
            <tr className="matrix-cta-row">
              <th scope="row" className="matrix-row-head">
                <span className="sr-only">Get started</span>
              </th>
              {plans.map((plan) => {
                const href = planHref(plan, interval, publicConfig);
                const external = href.startsWith("http") || href.startsWith("mailto:");
                return (
                  <td key={plan.id} className={plan.recommendation ? "is-featured" : ""}>
                    <a
                      className={`button ${plan.recommendation ? "button-primary" : "button-secondary"} matrix-cta`}
                      href={href}
                      onClick={external ? undefined : navigate(href)}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noopener" : undefined}
                    >
                      {matrixCtaLabel(plan)}
                    </a>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PlanTerms() {
  return (
    <section className="plan-terms" aria-labelledby="plan-terms-heading" id="plan-terms">
      <div className="section-heading center">
        <p className="eyebrow">Plan terms</p>
        <h2 id="plan-terms-heading">What the limits and labels mean</h2>
      </div>
      <dl className="plan-terms-grid">
        {planDefinitions.map((item) => (
          <div className="plan-term" key={item.id} id={`term-${item.id}`}>
            <dt>{item.term}</dt>
            <dd>{item.text}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function PricingFaq() {
  return (
    <section className="faq-section" aria-label="Pricing FAQ">
      <div className="section-heading center">
        <p className="eyebrow">Questions</p>
        <h2>Pricing FAQ</h2>
      </div>
      <div className="faq-accordion">
        {pricingFaq.map((item, i) => (
          <details className="faq-item" key={item.q} open={i === 0}>
            <summary>
              {item.q}
              <ChevronDown className="faq-chevron" size={20} aria-hidden="true" />
            </summary>
            <div className="faq-item-body">{item.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}

function CtaBand({ navigate }: { navigate: Navigate }) {
  return (
    <section className="cta-band reveal">
      <div className="cta-inner">
        <div>
          <h2>Put your file transfers on autopilot.</h2>
          <p>Download free and run your first managed transfer in minutes — or talk to us about an enterprise rollout. No brittle scripts required.</p>
        </div>
        <div className="cta-actions">
          <a className="button button-primary button-lg" href="/download" onClick={navigate("/download")}>
            <Download size={18} /> Start free
          </a>
          <a className="button button-ghost button-lg" href="/contact-sales" onClick={navigate("/contact-sales")}>
            <Mail size={18} /> Contact sales
          </a>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Download / activation / support                                     */
/* ------------------------------------------------------------------ */

function DownloadPage({ navigate, publicConfig, manifest }: { navigate: Navigate; publicConfig: PublicConfig; manifest: ReleaseManifest | null }) {
  return (
    <section className="shell page-section download-layout">
      <div className="download-copy">
        <p className="eyebrow eyebrow-pill">
          <Download size={15} /> Download
        </p>
        <h1>Install FileAgent on the server that runs your file workflows.</h1>
        <p className="lead">
          Choose the recommended Linux Debian package or the Windows EXE installer. Every download requires Terms acceptance before the package starts.
        </p>
        <DownloadOptions manifest={manifest} publicDownloadUrl={publicConfig.downloadUrl || configuredDownloadUrl(publicConfig)} />
        <a className="button button-secondary purchase-link" href="/pricing" onClick={navigate("/pricing")}>
          Purchase a license <ArrowRight size={17} />
        </a>
      </div>
      <aside className="install-panel" aria-label="Get started steps">
        <h2>Get started</h2>
        <ol className="step-list">
          <li>
            <span>1</span>
            <div>
              <strong>Download the package</strong>
              <p>Grab the Linux .deb package or Windows EXE installer for your environment.</p>
            </div>
          </li>
          <li>
            <span>2</span>
            <div>
              <strong>Install FileAgent</strong>
              <p>Run it on a server or workstation that can reach your source and destination systems.</p>
            </div>
          </li>
          <li>
            <span>3</span>
            <div>
              <strong>Open the web UI</strong>
              <p>Create credentials, define jobs, configure schedules, and monitor runs.</p>
            </div>
          </li>
          <li>
            <span>4</span>
            <div>
              <strong>Activate paid features</strong>
              <p>Paste your license key in FileAgent settings or import a signed offline license file.</p>
            </div>
          </li>
        </ol>
        <div className="license-callout">
          <KeyRound size={18} />
          <p>Free features work without purchase. Paid connectors, higher limits, logs, and admin features require a valid license.</p>
        </div>
      </aside>
    </section>
  );
}

function LicenseEmailHelp({ navigate }: { navigate: Navigate }) {
  const [resendOpen, setResendOpen] = useState(false);
  return (
    <>
      <div className="activation-state">
        <span className="activation-state-icon">
          <Mail size={22} />
        </span>
        <div>
          <h2>Your license key is in your email</h2>
          <p>
            When you completed checkout, we emailed your license key to the address you used — from{" "}
            <strong>noreply@fileagent.app</strong>. Look in your inbox (and spam folder) for a message with “FileAgent license” in
            the subject.
          </p>
        </div>
      </div>
      <ol className="activation-tips">
        <li>
          <span className="tick">
            <Check size={14} />
          </span>
          <span>Open the FileAgent email titled “Your FileAgent … license”.</span>
        </li>
        <li>
          <span className="tick">
            <Check size={14} />
          </span>
          <span>Copy the license key shown inside it.</span>
        </li>
        <li>
          <span className="tick">
            <Check size={14} />
          </span>
          <span>
            In FileAgent, open <strong>Settings → Licensing → Activate license</strong> and paste it in.
          </span>
        </li>
      </ol>

      <div className="resend-block">
        <h3>Resend your activation email</h3>
        <p>
          Can't find it? Open FileAgent and go to <strong>System Status</strong> to find your <strong>Customer ID</strong>. Enter
          that ID and we'll resend your activation email to the billing address associated with your license.
        </p>
        <div className="activation-actions">
          <button className="button button-primary" type="button" onClick={() => setResendOpen(true)}>
            <RefreshCw size={17} /> Resend License
          </button>
          <a className="button button-secondary" href="/download" onClick={navigate("/download")}>
            <Download size={17} /> Download FileAgent
          </a>
        </div>
      </div>

      {resendOpen && <ResendLicenseModal onClose={() => setResendOpen(false)} />}
    </>
  );
}

function ResendLicenseModal({ onClose }: { onClose: () => void }) {
  const [customerId, setCustomerId] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "success">("idle");
  const [error, setError] = useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
    document.body.classList.add("modal-open");
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.classList.remove("modal-open");
    };
  }, [onClose]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (state === "submitting") return;
    const id = customerId.trim();
    if (!id) {
      setError("Please enter your Customer ID.");
      inputRef.current?.focus();
      return;
    }
    setError(null);
    track("license_resend_submitted");
    setState("submitting");
    const result = await resendLicense(id);
    track(result.status === "ok" ? "license_resend_succeeded" : "license_resend_failed");
    if (result.status === "ok") {
      setState("success");
    } else if (result.status === "not_found") {
      setState("idle");
      setError(
        "We could not find a customer with that Customer ID. Please verify the Customer ID from the System Status page and try again.",
      );
    } else {
      setState("idle");
      setError("We could not resend the activation email right now. Please try again or contact support.");
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="resend-modal" role="dialog" aria-modal="true" aria-labelledby="resend-modal-title">
        {state === "success" ? (
          <div className="resend-success">
            <span className="resend-success-icon">
              <CheckCircle2 size={28} />
            </span>
            <h2 id="resend-modal-title">Activation email sent</h2>
            <p>Please check the email address associated with your FileAgent license.</p>
            <button className="button button-primary" type="button" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <span className="icon-badge">
                <RefreshCw size={18} />
              </span>
              <button className="icon-button modal-close" type="button" onClick={onClose} aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <h2 id="resend-modal-title">Resend Activation Email</h2>
            <p className="resend-modal-desc">
              Enter the Customer ID shown on the <strong>System Status</strong> page inside your FileAgent app. If it matches an
              active customer in our licensing system, we'll resend the activation email to the address on file.
            </p>
            <form onSubmit={submit} noValidate>
              <div className={`field ${error ? "has-error" : ""}`}>
                <label htmlFor="resend-customer-id">Customer ID</label>
                <input
                  id="resend-customer-id"
                  ref={inputRef}
                  type="text"
                  value={customerId}
                  onChange={(event) => {
                    setCustomerId(event.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="cus_…"
                  autoComplete="off"
                  spellCheck={false}
                  aria-invalid={!!error}
                  disabled={state === "submitting"}
                />
                {error && (
                  <span className="field-error" role="alert">
                    {error}
                  </span>
                )}
              </div>
              <div className="modal-actions">
                <button className="button button-secondary" type="button" onClick={onClose} disabled={state === "submitting"}>
                  Cancel
                </button>
                <button className="button button-primary" type="submit" disabled={state === "submitting"}>
                  {state === "submitting" ? (
                    <>
                      <span className="spinner" aria-hidden="true" /> Sending…
                    </>
                  ) : (
                    <>
                      <RefreshCw size={17} /> Resend Activation Email
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function ActivationPage({ navigate, publicConfig, manifest }: { navigate: Navigate; publicConfig: PublicConfig; manifest: ReleaseManifest | null }) {
  const [sessionId] = useState(() => new URLSearchParams(window.location.search).get("session_id") || "");
  const [license, setLicense] = useState<LicenseLookupResult | null>(null);
  const [copied, setCopied] = useState(false);
  const downloadUrl = license?.downloadUrl || configuredDownloadUrl(publicConfig);

  useEffect(() => {
    if (!sessionId) {
      setLicense(null);
      return;
    }

    let active = true;
    setLicense(null);
    fetchLicenseForSession(sessionId).then((result) => {
      if (active) {
        setLicense(result);
        track(result.status === "ready" ? "purchase_return_ready" : result.status === "pending" ? "purchase_return_pending" : "purchase_return_failed");
      }
    }).catch(() => {
      if (active) { track("purchase_return_failed"); setLicense({ status: "failed", message: "The licensing service could not load this purchase." }); }
    });
    return () => {
      active = false;
    };
  }, [sessionId]);

  const copyKey = async () => {
    if (!license?.licenseKey) return;
    await navigator.clipboard.writeText(license.licenseKey);
    track("license_key_copied");
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const status = license?.status;
  const loading = !!sessionId && license === null;

  const heading =
    status === "ready"
      ? "Your FileAgent license is ready"
      : loading
        ? "Preparing your license…"
        : status === "pending"
          ? "Your license is almost ready"
          : "Activate your FileAgent license";

  const subhead =
    status === "ready"
      ? "Copy your key below, then activate FileAgent in a couple of minutes."
      : loading
        ? "We're securely retrieving your purchase details. This only takes a few seconds."
        : status === "pending"
          ? "Your purchase is still finalizing and your license email is on its way — no need to buy again."
          : "Your license key was emailed to you when you purchased. Here's how to activate FileAgent in minutes.";

  return (
    <section className="shell page-section activation-page">
      <div className="section-heading center activation-head">
        <p className="eyebrow eyebrow-pill">
          <KeyRound size={15} /> License activation
        </p>
        <h1>{heading}</h1>
        <p>{subhead}</p>
      </div>

      <div className="activation-grid">
        <div className="activation-main">
          {status === "ready" ? (
            <div className="activation-panel">
              <div className="activation-state ok">
                <span className="activation-state-icon">
                  <BadgeCheck size={22} />
                </span>
                <div>
                  <h2>Your license key</h2>
                  <p>Paste this into FileAgent under Settings → Licensing → Activate license.</p>
                </div>
              </div>
              <div className="license-key-box">
                <code>{license?.licenseKey}</code>
                <button className="icon-button" type="button" onClick={copyKey} aria-label="Copy license key">
                  {copied ? <Check size={18} /> : <Copy size={18} />}
                </button>
              </div>
              {(license?.planName || license?.expiresAt) && (
                <div className="activation-meta">
                  {license?.planName && (
                    <span>
                      <BadgeCheck size={14} /> {license.planName}
                    </span>
                  )}
                  {license?.expiresAt && (
                    <span>
                      <TimerReset size={14} /> Renews {license.expiresAt}
                    </span>
                  )}
                </div>
              )}
              <div className="activation-actions">
                <a className="button button-secondary" href="/download" onClick={navigate("/download")}>
                  <ListChecks size={17} /> Setup steps
                </a>
              </div>
              <DownloadOptions manifest={manifest} publicDownloadUrl={downloadUrl} compact />
            </div>
          ) : loading ? (
            <div className="activation-panel">
              <div className="activation-state">
                <span className="activation-state-icon spin-host">
                  <RefreshCw size={22} />
                </span>
                <div>
                  <h2>Retrieving your license…</h2>
                  <p>Checking your purchase with our licensing service.</p>
                </div>
              </div>
              <div className="loading-bar" aria-hidden="true" />
            </div>
          ) : (
            <div className="activation-panel">
              {status === "pending" && (
                <div className="activation-status info" role="status">
                  <RefreshCw size={17} />
                  <span>{license?.message || "Your purchase is still finalizing. This usually clears within a minute."}</span>
                </div>
              )}
              {status === "failed" && (
                <div className="activation-status info" role="status">
                  <Mail size={17} />
                  <span>We couldn't load this purchase automatically — but your license key was emailed to you at checkout.</span>
                </div>
              )}

              <LicenseEmailHelp navigate={navigate} />

              {status === "pending" && (
                <button
                  className="button button-secondary activation-refresh"
                  type="button"
                  onClick={() => {
                    setLicense(null);
                    fetchLicenseForSession(sessionId).then(setLicense);
                  }}
                >
                  <RefreshCw size={16} /> Check again
                </button>
              )}
            </div>
          )}
        </div>

        <aside className="activation-aside">
          <div className="activation-panel activation-panel-soft">
            <h2 className="aside-title">
              <ListChecks size={18} /> Activate in 3 steps
            </h2>
            <ol className="step-list">
              <li>
                <span>1</span>
                <div>
                  <strong>Download &amp; install FileAgent</strong>
                  <p>Run it on the server or workstation that reaches your file systems.</p>
                </div>
              </li>
              <li>
                <span>2</span>
                <div>
                  <strong>Open Settings → Licensing</strong>
                  <p>Choose Activate license in the FileAgent web console.</p>
                </div>
              </li>
              <li>
                <span>3</span>
                <div>
                  <strong>Paste your key</strong>
                  <p>Click Activate to unlock your plan's connectors and limits.</p>
                </div>
              </li>
            </ol>
          </div>

          <div className="license-callout">
            <KeyRound size={18} />
            <p>Free features work without a license. Paid connectors, higher limits, logs, and admin features require a valid key.</p>
          </div>

          <div className="activation-panel activation-panel-soft">
            <h2 className="aside-title">
              <Mail size={18} /> Need a hand?
            </h2>
            <div className="activation-help-links">
              <a href="/download" onClick={navigate("/download")}>
                <Download size={16} /> Download FileAgent <ArrowRight size={15} />
              </a>
              <a href="/pricing" onClick={navigate("/pricing")}>
                <BadgeCheck size={16} /> Plans &amp; pricing <ArrowRight size={15} />
              </a>
              <a href={`mailto:${siteConfig.supportEmail}?subject=FileAgent%20license%20help`}>
                <Mail size={16} /> Email {siteConfig.supportEmail} <ArrowRight size={15} />
              </a>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function SupportPage({ navigate }: { navigate: Navigate }) {
  return (
    <section className="shell page-section support-layout">
      <div className="section-heading center">
        <p className="eyebrow">Support</p>
        <h1>Help for deployment, licensing, and file workflows.</h1>
        <p>
          Use these starting points when evaluating or deploying FileAgent — or browse the full documentation at{" "}
          <a href={siteConfig.docsUrl}>docs.fileagent.app</a>.
        </p>
      </div>
      <div className="support-grid">
        <SupportCard
          icon={<FileText />}
          title="Documentation"
          body="The complete FileAgent knowledge base: setup, jobs, credentials, alerts, security, troubleshooting, and FAQ."
          href={siteConfig.docsUrl}
        />
        <SupportCard
          icon={<Download />}
          title="Install & update"
          body="Download the package, install it on a host that can reach your file systems, then configure the web UI."
          href="/download"
          onClick={navigate("/download")}
        />
        <SupportCard
          icon={<KeyRound />}
          title="Licensing"
          body="After purchase, paste your license key into FileAgent settings or import a signed offline license file."
          href="/licensing/activation"
          onClick={navigate("/licensing/activation")}
        />
        <SupportCard
          icon={<Database />}
          title="Connectors"
          body="Plan connectors around network reachability, credential scope, and the plan level required for cloud endpoints."
        />
        <SupportCard
          icon={<SlidersHorizontal />}
          title="Jobs & scheduling"
          body="Define sources, destinations, transfer modes, schedules, retries, and conflict handling in the web console."
        />
        <SupportCard
          icon={<RotateCcw />}
          title="Backup & restore"
          body={`Schedule encrypted configuration backups and restore them when needed — available on ${availabilityText("config.backupRestore")}.`}
        />
        <SupportCard
          icon={<Mail />}
          title="Contact support"
          body={`Email ${siteConfig.supportEmail} for purchasing, activation, or deployment questions.`}
          href={`mailto:${siteConfig.supportEmail}`}
        />
      </div>
    </section>
  );
}

function SupportCard({
  icon,
  title,
  body,
  href,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  href?: string;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}) {
  const content = (
    <>
      <span className="icon-badge">{icon}</span>
      <h2>{title}</h2>
      <p>{body}</p>
    </>
  );
  if (href) {
    return (
      <a className="support-card" href={href} onClick={href.startsWith("/") ? onClick : undefined}>
        {content}
      </a>
    );
  }
  return <article className="support-card">{content}</article>;
}

/* ------------------------------------------------------------------ */
/* Footer                                                              */
/* ------------------------------------------------------------------ */

const footerColumns: { title: string; links: { label: string; href: string }[] }[] = [
  {
    title: "Product",
    links: [
      { label: "Features", href: "/#features" },
      { label: "Use cases", href: "/#use-cases" },
      { label: "Security", href: "/#security" },
      { label: "Pricing", href: "/pricing" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "For IT teams", href: "/file-transfer-automation-for-it-teams" },
      { label: "Self-hosted MFT", href: "/self-hosted-managed-file-transfer" },
      { label: "Scheduled SFTP", href: "/scheduled-sftp-transfers" },
      { label: "Replace PowerShell", href: "/replace-powershell-file-transfer-scripts" },
      { label: "SMB to S3", href: "/automated-smb-to-s3-transfer" },
      { label: "SFTP to Azure Blob", href: "/sftp-to-azure-blob-automation" },
      { label: "Robocopy alternative", href: "/robocopy-alternative-for-business" },
      { label: "WinSCP alternative", href: "/winscp-script-alternative" },
    ],
  },
  {
    title: "Compare",
    links: [
      { label: "vs. WinSCP scripts", href: "/compare/fileagent-vs-winscp-scripts" },
      { label: "vs. FileZilla", href: "/compare/fileagent-vs-filezilla" },
      { label: "vs. Robocopy", href: "/compare/fileagent-vs-robocopy" },
      { label: "vs. PowerShell + Scheduled Tasks", href: "/compare/fileagent-vs-powershell-scheduled-tasks" },
      { label: "vs. Enterprise MFT", href: "/compare/fileagent-vs-enterprise-mft" },
      { label: "vs. Cloud sync tools", href: "/compare/fileagent-vs-cloud-sync-tools" },
      { label: "vs. GoAnywhere (for SMBs)", href: "/compare/fileagent-vs-goanywhere-for-smbs" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Download", href: "/download" },
      { label: "Documentation", href: siteConfig.docsUrl },
      { label: "Support", href: "/support" },
      { label: "License activation", href: "/licensing/activation" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "Privacy", href: "/privacy" },
      { label: "Terms", href: "/terms" },
      { label: "Contact sales", href: "/contact-sales" },
    ],
  },
];

function Footer({ navigate }: { navigate: Navigate }) {
  return (
    <footer className="footer">
      <div className="shell footer-inner">
        <div className="footer-brand-col">
          <a className="brand footer-brand" href="/" onClick={navigate("/")} aria-label="FileAgent home">
            <BrandLogo />
          </a>
          <p>Reliable, self-hosted file transfer automation for IT teams and technical operators.</p>
          <a className="button button-primary footer-cta" href="/download" onClick={navigate("/download")}>
            <Download size={16} /> Get FileAgent
          </a>
        </div>
        <div className="footer-cols">
          {footerColumns.map((column) => (
            <div className="footer-col" key={column.title}>
              <h4>{column.title}</h4>
              <ul>
                {column.links.map((link) => {
                  const internal = link.href.startsWith("/");
                  return (
                    <li key={link.label}>
                      <a href={link.href} onClick={internal ? navigate(link.href) : undefined}>
                        {link.label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className="shell footer-bottom">
        <span>© {new Date().getFullYear()} FileAgent. All rights reserved.</span>
        <span className="footer-bottom-links">
          <a href="/privacy" onClick={navigate("/privacy")}>
            Privacy
          </a>
          <a href="/terms" onClick={navigate("/terms")}>
            Terms
          </a>
        </span>
      </div>
    </footer>
  );
}

/* ------------------------------------------------------------------ */
/* Bootstrap                                                           */
/* ------------------------------------------------------------------ */

declare global {
  interface Window {
    __fileAgentRoot?: ReturnType<typeof createRoot>;
  }
}

// Reuse a single root so Vite HMR (and accidental re-imports) never call
// createRoot twice on the same container. In production this runs once.
const container = document.getElementById("root")!;
const root = window.__fileAgentRoot ?? createRoot(container);
window.__fileAgentRoot = root;
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
